import { useCallback, useEffect, useRef, useState } from 'react'
import { paireAppareil, clePubliqueBase64, chaineCanonique, signer, effacerCleAppareil } from './device-key.js'
import './pointeuse.css'

/**
 * BORNE DE POINTAGE — CLIENT WEB DE RECETTE.
 *
 * ═══ CE QUE CE CLIENT EST, ET CE QU'IL N'EST PAS ═══
 *
 * Le client de PRODUCTION visé est l'application iPad native. Cette page web
 * existe pour éprouver le contrat `/api/v1/kiosk/v1/*` de bout en bout dans un
 * navigateur — appairer, saisir un code, signer, pointer, traiter une annonce
 * bloquante — sans attendre Xcode.
 *
 * ELLE SIGNE SES REQUÊTES COMME L'APPLICATION NATIVE. La clé privée est
 * générée par WebCrypto avec `extractable: false` : elle ne peut plus jamais
 * sortir du navigateur, pas même par la console. C'est ce qui permet de ne PAS
 * maintenir une route moins sûre à côté du contrat — il n'y en a qu'un.
 *
 * ELLE N'A PAS DE MODE HORS LIGNE, et c'est un choix explicite. Le hors ligne
 * du nouveau contrat repose sur une clé propre à l'appareil, remise une seule
 * fois à l'appairage et destinée au trousseau sécurisé d'iOS. Un navigateur ne
 * sait pas garder un tel secret : le stockage local est lisible par quiconque
 * tient la machine. Reconstruire ici un hors ligne « presque sûr » reviendrait
 * à réintroduire exactement le trousseau qu'on vient de supprimer. Sans
 * réseau, cette page le DIT et refuse d'inventer une identification.
 */

const K_TOKEN = 'gestion.pointeuse.token'
const K_STATION = 'gestion.pointeuse.station'
const K_KEYID = 'gestion.pointeuse.keyid'

interface Station { id: string; name: string; location_name: string | null; allowed_methods: string[] }
interface Shift {
  shift_id: string | null; planned: boolean; starts_at: string; ends_at: string
  break_minutes: number; break_paid: boolean
  position_name: string | null; location_name: string | null; event_name: string | null
}
interface Capabilities {
  can_clock_in: boolean; can_start_break: boolean; can_end_break: boolean; can_clock_out: boolean
  can_view_own_schedule: boolean; can_view_team_schedule: boolean; can_view_team_presence: boolean
}
interface Annonce {
  receipt_id: string; title: string; body: string; ack_mode: 'NONE' | 'ACKNOWLEDGE' | 'SIGNATURE'
  consent_text: string | null
}
interface Contexte {
  contract_version: string; server_time: string
  person: { person_id: string; display_name: string; position_name: string | null; photo_available: boolean }
  state: string
  shift: Shift | null
  clock_in_at: string | null
  current_break: { starts_at: string; minutes: number } | null
  summary: { presence_minutes: number; break_minutes: number; paid_minutes: number }
  upcoming_shifts: Shift[]
  blocking_announcement: Annonce | null
  capabilities: Capabilities
}
type Action = 'CLOCK_IN' | 'BREAK_START' | 'BREAK_END' | 'CLOCK_OUT'

const ETAT_LABEL: Record<string, string> = {
  OFF_SHIFT: 'Hors service', UPCOMING_SHIFT: 'Service à venir', EXPECTED_LATE: 'Service commencé',
  WORKING: 'En poste', WORKING_UNPLANNED: 'En poste — service non prévu', ON_BREAK: 'En pause', FINISHED: 'Service terminé',
}
const ACTION_LABEL: Record<Action, string> = {
  CLOCK_IN: 'Prendre mon poste', BREAK_START: 'Partir en pause',
  BREAK_END: 'Reprendre mon poste', CLOCK_OUT: 'Terminer mon service',
}

const heure = (iso: string | null): string =>
  iso ? new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' }).format(new Date(iso)) : '—'

const nonce = (): string =>
  (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)

/** Le tracé part normalisé 0..1 : le serveur ne connaît que des proportions. */
interface Traits { w: number; h: number; s: Array<Array<[number, number]>> }

function Signature({ onChange }: { onChange: (t: Traits | null) => void }) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  const traits = useRef<Array<Array<[number, number]>>>([])
  const encours = useRef<Array<[number, number]> | null>(null)

  const peindre = useCallback(() => {
    const c = ref.current; if (!c) return
    const ctx = c.getContext('2d'); if (!ctx) return
    ctx.clearRect(0, 0, c.width, c.height)
    ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round'
    ctx.strokeStyle = getComputedStyle(c).color
    for (const t of [...traits.current, ...(encours.current ? [encours.current] : [])]) {
      ctx.beginPath()
      t.forEach(([x, y], i) => { const px = x * c.width, py = y * c.height; i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) })
      ctx.stroke()
    }
  }, [])

  const pointDe = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const r = e.currentTarget.getBoundingClientRect()
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))]
  }

  const publier = () => {
    const total = traits.current.reduce((n, t) => n + t.length, 0)
    onChange(total >= 4 ? { w: 5, h: 2, s: traits.current.map((t) => [...t]) } : null)
  }

  return (
    <div className="pk-signature">
      <canvas
        ref={ref} width={600} height={240} className="pk-canvas"
        aria-label="Zone de signature manuscrite"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); encours.current = [pointDe(e)]; peindre() }}
        onPointerMove={(e) => { if (!encours.current) return; encours.current.push(pointDe(e)); peindre() }}
        onPointerUp={() => { if (encours.current && encours.current.length > 1) traits.current.push(encours.current); encours.current = null; peindre(); publier() }}
        onPointerCancel={() => { encours.current = null; peindre() }}
      />
      <button type="button" className="pk-effacer"
        onClick={() => { traits.current = []; encours.current = null; peindre(); onChange(null) }}>
        Effacer
      </button>
    </div>
  )
}

export function PointeuseKiosk() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(K_TOKEN))
  const [station, setStation] = useState<Station | null>(() => {
    try { return JSON.parse(localStorage.getItem(K_STATION) ?? 'null') as Station | null } catch { return null }
  })
  const [code, setCode] = useState('')
  const [nom, setNom] = useState('Borne de recette')
  const [pin, setPin] = useState('')
  const [proof, setProof] = useState<string | null>(null)
  const [ctx, setCtx] = useState<Contexte | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const [trace, setTrace] = useState<Traits | null>(null)
  const [action, setAction] = useState<Action | null>(null)
  const [reseau, setReseau] = useState(true)

  const appel = useCallback(async (chemin: string, init: RequestInit = {}, avecProof = false): Promise<{ ok: boolean; data: Record<string, unknown> }> => {
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (token) headers['x-clock-station-token'] = token
    if (avecProof && proof) headers['x-clock-interaction'] = proof

    // SIGNATURE D'APPAREIL — sur tout sauf l'appairage, où la borne n'existe
    // pas encore et où il n'y a donc rien à signer.
    const stationId = station?.id
    const keyId = localStorage.getItem(K_KEYID)
    if (chemin !== 'pair' && stationId && keyId) {
      const paire = await paireAppareil()
      const timestamp = new Date().toISOString()
      const requestNonce = nonce()
      const idx = chemin.indexOf('?')
      const canonique = await chaineCanonique({
        method: (init.method ?? 'GET').toUpperCase(),
        path: `/api/v1/kiosk/v1/${idx === -1 ? chemin : chemin.slice(0, idx)}`,
        query: idx === -1 ? '' : chemin.slice(idx + 1),
        body: typeof init.body === 'string' ? init.body : '',
        stationId, keyId, timestamp, nonce: requestNonce,
      })
      headers['x-device-sig-version'] = 'gcs1'
      headers['x-device-key-id'] = keyId
      headers['x-device-timestamp'] = timestamp
      headers['x-device-nonce'] = requestNonce
      headers['x-device-signature'] = await signer(paire, canonique)
    }
    try {
      const r = await fetch(`/api/v1/kiosk/v1/${chemin}`, { ...init, headers })
      setReseau(true)
      const data = (await r.json().catch(() => ({}))) as Record<string, unknown>
      // ① SI LA BORNE EST RÉVOQUÉE, ON PURGE. Le serveur le demande
      //   explicitement — une borne muette qui garde son cache reste un risque.
      if (data['purge_required'] === true) {
        localStorage.removeItem(K_TOKEN); localStorage.removeItem(K_STATION); localStorage.removeItem(K_KEYID)
        // La clé d'appareil part avec le reste : une borne révoquée doit être
        // réappairée, donc redevenir un appareil inconnu.
        void effacerCleAppareil()
        setToken(null); setStation(null); setProof(null); setCtx(null)
        setMessage('Cette borne a été révoquée. Données locales effacées.')
        return { ok: false, data }
      }
      return { ok: r.ok, data }
    } catch {
      // ② PAS DE RÉSEAU : on le DIT. Ce client n'a pas de mode hors ligne, et
      //   il ne prétend pas en avoir un.
      setReseau(false)
      return { ok: false, data: { message: 'Réseau indisponible — ce client de recette ne fonctionne pas hors ligne.' } }
    }
  }, [token, proof])

  const appairer = async () => {
    setEnCours(true)
    // La paire est générée AVANT l'appairage : seule la clé publique part.
    const paire = await paireAppareil()
    const pub = await clePubliqueBase64(paire)
    const r = await appel('pair', {
      method: 'POST',
      body: JSON.stringify({ code: code.trim().toUpperCase(), name: nom.trim(), version: 'web-qa-2', device_public_key: pub }),
    })
    setEnCours(false)
    if (!r.ok) { setMessage(String(r.data['message'] ?? 'Appairage refusé.')); return }
    localStorage.setItem(K_TOKEN, String(r.data['token']))
    localStorage.setItem(K_KEYID, String(r.data['device_key_id']))
    // L'identifiant de borne est conservé DÈS L'APPAIRAGE : la première
    // synchronisation doit déjà être signée, et une signature a besoin de
    // savoir quelle borne elle désigne. Sans cela, rien ne pourrait démarrer.
    const amorce = {
      id: String(r.data['station_id']), name: nom.trim(),
      location_name: null, allowed_methods: ['PIN'],
    }
    localStorage.setItem(K_STATION, JSON.stringify(amorce))
    setStation(amorce)
    setToken(String(r.data['token']))
    setMessage(null); setCode('')
    // La clé hors ligne renvoyée par le serveur est volontairement IGNORÉE ici :
    // un navigateur ne saurait pas la garder à l'abri.
  }

  const synchroniser = useCallback(async () => {
    if (!token) return
    const r = await appel('sync', { method: 'POST', body: JSON.stringify({ version: 'web-qa-1' }) })
    if (r.ok && r.data['station']) {
      const s = r.data['station'] as Station
      setStation(s); localStorage.setItem(K_STATION, JSON.stringify(s))
    }
  }, [token, appel])
  useEffect(() => { void synchroniser() }, [synchroniser])

  const identifier = async () => {
    setEnCours(true)
    const r = await appel('identify', { method: 'POST', body: JSON.stringify({ method: 'PIN', secret: pin }) })
    setEnCours(false); setPin('')
    if (!r.ok) { setMessage(String(r.data['message'] ?? 'Code refusé.')); return }
    const inter = r.data['interaction'] as { token: string }
    setProof(inter.token); setCtx(r.data as unknown as Contexte); setMessage(null); setAction(null); setTrace(null)
  }

  const pointer = async () => {
    if (!action || !trace) return
    setEnCours(true)
    const r = await appel('clock', {
      method: 'POST',
      body: JSON.stringify({ action, device_time: new Date().toISOString(), nonce: nonce(), method: 'PIN', signature: trace }),
    }, true)
    setEnCours(false)
    if (!r.ok) { setMessage(String(r.data['message'] ?? 'Action refusée.')); return }
    setTrace(null); setAction(null)
    if (action === 'CLOCK_OUT') { setProof(null); setCtx(null); setMessage('Bonne fin de service.'); return }
    setCtx((r.data['context'] as Contexte) ?? null)
  }

  const repondreAnnonce = async (signer: boolean) => {
    const a = ctx?.blocking_announcement; if (!a) return
    if (signer && !trace) return
    setEnCours(true)
    const r = signer
      ? await appel('announcements/sign', {
          method: 'POST',
          body: JSON.stringify({ receipt_id: a.receipt_id, device_time: new Date().toISOString(), nonce: nonce(), signature: trace }),
        }, true)
      : await appel('announcements/refuse', {
          method: 'POST', body: JSON.stringify({ receipt_id: a.receipt_id, device_time: new Date().toISOString() }),
        }, true)
    setEnCours(false)
    if (!r.ok) { setMessage(String(r.data['message'] ?? 'Réponse refusée.')); return }
    setTrace(null)
    setCtx((r.data['context'] as Contexte) ?? null)
  }

  /* ── Appairage ── */
  if (!token) {
    return (
      <div className="pk-ecran pk-ecran--appairage">
        <h1>Associer cette borne</h1>
        <p className="pk-sous">Un administrateur génère un code depuis Équipe → Temps → Pointeuse.</p>
        <input className="pk-champ" value={nom} onChange={(e) => setNom(e.target.value)} aria-label="Nom de la borne" />
        <input className="pk-champ pk-champ--code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="CODE" maxLength={8} aria-label="Code d’appairage" />
        <button type="button" className="pk-action" onClick={() => void appairer()} disabled={enCours || code.length < 6}>
          Associer
        </button>
        {message ? <p className="pk-message">{message}</p> : null}
      </div>
    )
  }

  /* ── Écran d'accueil : aucun nom, aucun planning avant identification ── */
  if (!ctx) {
    return (
      <div className="pk-ecran">
        <header className="pk-entete">
          <span>{station?.name ?? 'Borne'}</span>
          <span className={reseau ? 'pk-reseau' : 'pk-reseau pk-reseau--ko'}>{reseau ? 'En ligne' : 'Hors ligne'}</span>
        </header>
        <h1>Votre code</h1>
        <input className="pk-champ pk-champ--pin" value={pin} inputMode="numeric" maxLength={4}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} aria-label="Code personnel à 4 chiffres" />
        <button type="button" className="pk-action" onClick={() => void identifier()} disabled={enCours || pin.length !== 4}>
          Valider
        </button>
        {!reseau ? (
          <p className="pk-message">
            Ce client de recette ne fonctionne pas hors ligne : il ne conserve aucun élément
            permettant de vérifier un code sans le serveur. C’est délibéré.
          </p>
        ) : null}
        {message ? <p className="pk-message">{message}</p> : null}
      </div>
    )
  }

  /* ── Annonce bloquante : signer ou refuser, rien d'autre ── */
  const a = ctx.blocking_announcement
  if (a) {
    return (
      <div className="pk-ecran pk-ecran--annonce">
        <h1>{a.title}</h1>
        <p className="pk-corps">{a.body}</p>
        <p className="pk-sous">{a.consent_text}</p>
        <Signature onChange={setTrace} />
        <div className="pk-boutons">
          <button type="button" className="pk-action" onClick={() => void repondreAnnonce(true)} disabled={enCours || !trace}>
            Lu et approuvé
          </button>
          {/* Refuser ne demande aucun tracé, et ne bloque pas le pointage ensuite. */}
          <button type="button" className="pk-action pk-action--secondaire" onClick={() => void repondreAnnonce(false)} disabled={enCours}>
            Refuser de signer
          </button>
        </div>
        {message ? <p className="pk-message">{message}</p> : null}
      </div>
    )
  }

  const possibles: Action[] = ([
    ctx.capabilities.can_clock_in ? 'CLOCK_IN' : null,
    ctx.capabilities.can_start_break ? 'BREAK_START' : null,
    ctx.capabilities.can_end_break ? 'BREAK_END' : null,
    ctx.capabilities.can_clock_out ? 'CLOCK_OUT' : null,
  ].filter(Boolean) as Action[])

  return (
    <div className="pk-ecran pk-ecran--personne">
      <header className="pk-entete">
        <span>{station?.name ?? 'Borne'}</span>
        <button type="button" className="pk-quitter" onClick={() => { setCtx(null); setProof(null); setTrace(null); setAction(null) }}>
          Fermer
        </button>
      </header>
      <h1>{ctx.person.display_name}</h1>
      <p className="pk-sous">{ctx.person.position_name ?? '—'} · {ETAT_LABEL[ctx.state] ?? ctx.state}</p>

      {ctx.shift ? (
        <p className="pk-shift">
          {heure(ctx.shift.starts_at)} – {heure(ctx.shift.ends_at)}
          {ctx.shift.location_name ? ` · ${ctx.shift.location_name}` : ''}
          {!ctx.shift.planned ? ' · non prévu' : ''}
          {ctx.shift.break_minutes > 0
            ? ` · pause ${ctx.shift.break_minutes} min ${ctx.shift.break_paid ? 'rémunérée' : 'non rémunérée'}`
            : ''}
        </p>
      ) : <p className="pk-shift">Aucun service publié aujourd’hui.</p>}

      {ctx.clock_in_at ? (
        <p className="pk-sous">Entrée à {heure(ctx.clock_in_at)}
          {ctx.current_break ? ` · en pause depuis ${ctx.current_break.minutes} min` : ''}</p>
      ) : null}

      {action ? (
        <>
          <p className="pk-sous">{ACTION_LABEL[action]} — signez pour confirmer.</p>
          <Signature onChange={setTrace} />
          <div className="pk-boutons">
            <button type="button" className="pk-action" onClick={() => void pointer()} disabled={enCours || !trace}>
              Confirmer
            </button>
            <button type="button" className="pk-action pk-action--secondaire" onClick={() => { setAction(null); setTrace(null) }}>
              Annuler
            </button>
          </div>
        </>
      ) : (
        <div className="pk-boutons">
          {possibles.length === 0
            ? <p className="pk-sous">Aucune action possible pour le moment.</p>
            : possibles.map((k) => (
              <button key={k} type="button" className="pk-action" onClick={() => { setAction(k); setTrace(null) }}>
                {ACTION_LABEL[k]}
              </button>
            ))}
        </div>
      )}

      {ctx.upcoming_shifts.length > 0 ? (
        <section className="pk-prochains">
          <h2>Mes prochains services</h2>
          <ul>
            {ctx.upcoming_shifts.map((s, i) => (
              <li key={i}>
                {new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Paris' }).format(new Date(s.starts_at))}
                {' · '}{heure(s.starts_at)}–{heure(s.ends_at)}
                {s.location_name ? ` · ${s.location_name}` : ''}
                {s.event_name ? ` · ${s.event_name}` : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {message ? <p className="pk-message">{message}</p> : null}
    </div>
  )
}
